import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useChat } from '../../context/ChatContext';
import { usersAPI, convsAPI } from '../../utils/api';
import { formatChatDate, getInitials, getAvatarColor } from '../../utils/helpers';
import Avatar from '../UI/Avatar';

const LOGO = '/img/noris-logo.svg';

export default function Sidebar({ onNewChat, onNewGroup, onProfile, onSettings }) {
  const { user, logout }                                       = useAuth();
  const { conversations, loadConversations, loadArchivedConversations,
          activeConvId, openConversation, tab, setTab,
          weighmentUnread, setWeighmentUnread } = useChat();
  const [search, setSearch]                                    = useState('');
  const [searchResults, setSearchResults]                      = useState([]);
  const [searching, setSearching]                              = useState(false);
  const [showArchived, setShowArchived]                        = useState(false);
  const [archived, setArchived]                                = useState([]);
  const [loadingArchived, setLoadingArchived]                  = useState(false);

  useEffect(() => { 
    if (conversations.length === 0) loadConversations(); 
  }, [loadConversations, conversations.length]);

  useEffect(() => {
    if (!search.trim() || search.length < 2) { setSearchResults([]); return; }
    const t = setTimeout(async () => {
      setSearching(true);
      try {
        const res = await usersAPI.search(search);
        if (res.data.success) setSearchResults(res.data.data.users);
      } catch {}
      setSearching(false);
    }, 350);
    return () => clearTimeout(t);
  }, [search]);

  const startChat = async (userId) => {
    const res = await convsAPI.direct(userId);
    if (res.data.success) {
      await loadConversations();
      openConversation(res.data.data.conversation_id);
      setSearch('');
    }
  };

  const handleShowArchived = async () => {
    setLoadingArchived(true);
    const list = await loadArchivedConversations();
    setArchived(list);
    setShowArchived(true);
    setLoadingArchived(false);
  };

  const handleUnarchive = async (conv) => {
    await convsAPI.archive(conv.id, 0);
    const updated = archived.filter(c => c.id !== conv.id);
    setArchived(updated);
    await loadConversations();
    if (updated.length === 0) setShowArchived(false);
  };

  const filtered = search
    ? conversations.filter(c => c.name?.toLowerCase().includes(search.toLowerCase()))
    : conversations;

  const totalUnread = conversations.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <div className="sidebar">
      <div className="sidebar-header">
        <div className="logo">
          <img src={LOGO} alt="NChat" onError={e => { e.target.style.display='none'; }} />
          <span>NChat</span>
        </div>
        <div className="header-actions">
          <button className="icon-btn" title="New Chat" onClick={onNewChat}>💬</button>
          <button className="icon-btn" title="New Group" onClick={onNewGroup}>👥</button>
          <button className="icon-btn" title="Settings" onClick={onSettings}>⚙️</button>
          <button className="icon-btn" title="Profile" onClick={onProfile}>
            <Avatar user={user} size={28} />
          </button>
        </div>
      </div>

      {/* Tab Bar — includes Weighments tab */}
      <div className="tab-bar">
        {[
          { id: 'chats',      label: 'Chats',   icon: '💬', badge: totalUnread },
          { id: 'status',     label: 'Status',  icon: '⭕' },
          { id: 'calls',      label: 'Calls',   icon: '📞' },
          { id: 'weighments', label: 'Bills', icon: '⚖️', dot: weighmentUnread },
          { id: 'nearby',     label: 'Nearby',      icon: '📍' },
        ].map(t => (
          <button
            key={t.id}
            className={`tab-btn ${tab === t.id ? 'active' : ''}`}
            onClick={() => {
              setTab(t.id);
              if (t.id === 'weighments') setWeighmentUnread(false);
            }}
            style={{ position: 'relative' }}
          >
            <span className="tab-icon">{t.icon}</span>
            {t.label}
            {t.badge > 0 && tab !== t.id && (
              <span style={{
                position: 'absolute', top: 4, right: 4,
                background: 'var(--accent)', color: 'white',
                borderRadius: '50%', width: 16, height: 16,
                fontSize: 10, display: 'flex', alignItems: 'center', justifyContent: 'center',
                fontWeight: 700,
              }}>{t.badge > 99 ? '99+' : t.badge}</span>
            )}
            {t.dot && tab !== t.id && (
              <span style={{
                position: 'absolute', top: 8, right: 12,
                background: '#25D366', // WhatsApp Green
                borderRadius: '50%', width: 10, height: 10,
                boxShadow: '0 0 0 2px white',
              }} />
            )}
          </button>
        ))}
      </div>

      {/* Search — only for chats tab */}
      {tab === 'chats' && (
        <div className="search-bar">
          <div className="search-input-wrap">
            <span className="search-icon">🔍</span>
            <input className="search-input" placeholder="Search or start new chat"
              value={search} onChange={e => setSearch(e.target.value)} />
            {search && (
              <button onClick={() => setSearch('')}
                style={{ border: 'none', background: 'none', cursor: 'pointer', padding: '0 8px', color: '#667781' }}>✕</button>
            )}
          </div>
        </div>
      )}

      {/* Conversation list — only for chats tab */}
      {tab === 'chats' && (
        <div className="conv-list">
          {showArchived ? (
            <>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid var(--border)' }}>
                <button onClick={() => setShowArchived(false)}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--primary)', fontSize: 18 }}>◀</button>
                <span style={{ fontWeight: 700, fontSize: 15 }}>Archived Chats</span>
              </div>
              {archived.length === 0 && (
                <div className="empty-state">
                  <span className="icon">📦</span>
                  <h3>No archived chats</h3>
                </div>
              )}
              {archived.map(conv => (
                <div key={conv.id} className="conv-item" style={{ alignItems: 'center' }}>
                  <div style={{ flex: 1 }} onClick={() => { openConversation(conv.id); setShowArchived(false); }}>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>{conv.name}</div>
                    <div style={{ fontSize: 12, color: '#667781' }}>{conv.last_message || 'No messages'}</div>
                  </div>
                  <button onClick={() => handleUnarchive(conv)} className="btn btn-ghost btn-sm"
                    style={{ fontSize: 12, flexShrink: 0 }} title="Unarchive">📤 Unarchive</button>
                </div>
              ))}
            </>
          ) : (
            <>
              {search && (
                <>
                  {searching && (
                    <div style={{ padding: '16px', textAlign: 'center' }}>
                      <div className="spinner" style={{ width: 24, height: 24 }} />
                    </div>
                  )}
                  {searchResults.length > 0 && (
                    <>
                      <div style={{ padding: '8px 16px 4px', fontSize: 12, fontWeight: 700, color: '#667781', textTransform: 'uppercase', letterSpacing: '.5px' }}>People</div>
                      {searchResults.map(u => (
                        <div key={u.id} className="conv-item" onClick={() => startChat(u.id)}>
                          <div className="conv-avatar">
                            <Avatar user={u} size={48} />
                            {u.status === 'online' && <span className="online-dot" />}
                          </div>
                          <div className="conv-info">
                            <div className="conv-top"><span className="conv-name">{u.name}</span></div>
                            <div className="conv-bottom"><span className="conv-preview">{u.phone}</span></div>
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                  {searchResults.length > 0 && filtered.length > 0 && (
                    <div style={{ padding: '8px 16px 4px', fontSize: 12, fontWeight: 700, color: '#667781', textTransform: 'uppercase', letterSpacing: '.5px' }}>Chats</div>
                  )}
                  {search && !searching && searchResults.length === 0 && filtered.length === 0 && (
                    <p style={{ textAlign: 'center', color: '#667781', padding: 20, fontSize: 14 }}>No results found</p>
                  )}
                </>
              )}
              {filtered.map(conv => (
                <ConvItem key={conv.id} conv={conv} active={conv.id === activeConvId} onOpen={() => openConversation(conv.id)} />
              ))}
              {!search && conversations.length === 0 && (
                <div className="empty-state">
                  <span className="icon">💬</span>
                  <h3>No conversations yet</h3>
                  <p>Search for someone to start chatting</p>
                </div>
              )}
              {!search && (
                <div onClick={handleShowArchived}
                  style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '12px 16px', cursor: 'pointer', color: 'var(--primary)', fontSize: 14, fontWeight: 500, borderTop: '1px solid var(--border)', marginTop: 4 }}>
                  {loadingArchived ? <div className="spinner" style={{ width: 18, height: 18 }} /> : <span style={{ fontSize: 18 }}>📦</span>}
                  Archived Chats
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ConvItem({ conv, active, onOpen }) {
  const otherUser = conv.other_user;
  const isOnline  = otherUser?.status === 'online';
  const hasUnread = (conv.unread_count || 0) > 0;

  const preview = () => {
    if (!conv.last_message) return 'Tap to start chatting';
    const t = conv.last_message_type;
    if (t === 'image')  return '📷 Photo';
    if (t === 'video')  return '🎥 Video';
    if (t === 'audio' || t === 'voice') return '🎤 Voice message';
    if (t === 'file')   return '📎 File';
    if (t === 'call')   return '📞 Call';
    return conv.last_message;
  };

  return (
    <div className={`conv-item ${active ? 'active' : ''}`} onClick={onOpen}>
      <div className="conv-avatar">
        {conv.type === 'group'
          ? <GroupAvatar name={conv.name} />
          : <Avatar user={otherUser || { name: conv.name, avatar: conv.avatar }} size={50} />
        }
        {isOnline && <span className="online-dot" />}
      </div>
      <div className="conv-info">
        <div className="conv-top">
          <span className="conv-name" style={{ fontWeight: hasUnread ? 700 : 600 }}>{conv.name}</span>
          <span className="conv-time" style={{ color: hasUnread ? 'var(--primary)' : undefined, fontWeight: hasUnread ? 700 : 400 }}>
            {formatChatDate(conv.last_message_at)}
          </span>
        </div>
        <div className="conv-bottom">
          <span className="conv-preview" style={{ fontWeight: hasUnread ? 600 : 400, color: hasUnread ? '#111B21' : undefined }}>
            {preview()}
          </span>
          <div style={{ display: 'flex', alignItems: 'center', gap: 4, flexShrink: 0 }}>
            {conv.is_muted == 1 && <span style={{ fontSize: 13 }}>🔇</span>}
            {hasUnread && (
              <span className="unread-badge">{conv.unread_count > 99 ? '99+' : conv.unread_count}</span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function GroupAvatar({ name }) {
  return (
    <div style={{
      width: 48, height: 48, borderRadius: '50%',
      background: getAvatarColor(name),
      display: 'flex', alignItems: 'center', justifyContent: 'center',
      fontSize: 18, fontWeight: 700, color: 'white',
    }}>
      {getInitials(name)}
    </div>
  );
}
